// Shared email chrome in the HQ Communications welcome-email design; the template words are unchanged from rensure-sme-smp.

import type { BrandConfig } from "../brands";
import type { EmailData } from "../types";
import { escapeHtml, longDate, telHref } from "../format";

export type BrandedEmailArgs = {
  brand: BrandConfig;
  /** The template's send-time data — fills the "Your policy" card. */
  data?: EmailData;
  /** Pre-rendered, email-safe HTML for the message body (paragraphs). */
  bodyHtml: string;
  /** Brand call-centre number, e.g. "0333 202 6242". */
  callNumber: string;
};

// ─── Body copy helpers ────────────────────────────────────────────────────────

/** One body paragraph. */
export const p = (html: string): string => `<p style="margin:0 0 14px;">${html}</p>`;

/** Join the paragraphs of a message body. */
export const body = (...paragraphs: string[]): string => paragraphs.join("\n");

/** Bold run inside body copy (the **double asterisks** of the SMP editor). */
export const b = (html: string): string => `<strong>${html}</strong>`;

/** The brand's call number as a bold tap-to-call link inside body copy. */
export const call = (brand: BrandConfig): string =>
  `<a href="${telHref(brand.callNumber)}" style="color:${brand.primary};text-decoration:none;font-weight:700;white-space:nowrap;">${escapeHtml(brand.callNumber)}</a>`;

/** Escape a value that came from the customer record. */
export const esc = escapeHtml;

// ─── Building blocks ──────────────────────────────────────────────────────────

/** A full-width presentation table. */
export const table = (inner: string, style = ""): string =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"${style ? ` style="${style}"` : ""}>${inner}</table>`;

/** One full-width row of the email card. */
export const row = (inner: string, style: string, cls = ""): string =>
  `          <tr>
            <td${cls ? ` class="${cls}"` : ""} style="${style}">
              ${inner}
            </td>
          </tr>`;

/** Marks a design-only section (words not from the template) so previews and checks can tell them apart. */
const section = (name: string, html: string): string => `<!-- section:${name} -->${html}<!-- /section -->`;

/** A brand artwork image from the brand's asset folder. */
export const art = (brand: BrandConfig, file: string, width: number, alt = "", style = ""): string =>
  `<img src="${brand.assetBase}/${file}" width="${width}" alt="${escapeHtml(alt)}" border="0" style="display:block;border:0;outline:none;text-decoration:none;max-width:100%;height:auto;${style}">`;

/** The solid brand button (welcome-email style, Outlook-safe padding). */
export function button(brand: BrandConfig, label: string, url: string, outline = false): string {
  const fill = outline ? "#ffffff" : brand.primary;
  const ink = outline ? brand.primary : "#ffffff";
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" bgcolor="${fill}" style="border-radius:9px;border:2px solid ${brand.primary};mso-padding-alt:15px 24px;"><a href="${url}" style="display:block;padding:15px 24px;border-radius:9px;color:${ink};background:${fill};font-weight:700;font-size:16px;line-height:22px;text-align:center;text-decoration:none;">${label}&nbsp; &#8594;</a></td></tr></table>`;
}

/** Small uppercase, letter-spaced label in the brand colour. */
export function sectionLabel(text: string, accent: string): string {
  return `<p style="margin:30px 0 12px;font-size:11px;line-height:16px;letter-spacing:1.6px;font-weight:700;text-transform:uppercase;color:${accent};">${escapeHtml(text)}</p>`;
}

/** Two-line welcome-email headline, second line in the brand colour. */
const headline = (brand: BrandConfig, first: string, second: string, size = 32): string =>
  `<h2 style="margin:0;color:${brand.ink};font-size:${size}px;line-height:${size + 3}px;letter-spacing:-1px;font-weight:700;">${first}<br><span style="color:${brand.primary};">${second}</span></h2>`;

/** The standard "Not planning to renew?" call-to-inform block. */
export function notRenewingHtml(
  brand: BrandConfig,
  callNumber: string,
  /** Sentence tail after the number — overridden by the auto Direct Debit email. */
  tail = "so we can update your records.",
): string {
  return `<p style="margin:0;padding:14px 16px;background:${brand.soft};border-left:3px solid ${brand.primary};border-radius:0 10px 10px 0;font-size:13px;line-height:21px;color:${brand.text};">
                Not planning to renew? Please call our UK-based team on
                <a href="${telHref(callNumber)}" style="color:${brand.primary};text-decoration:none;font-weight:700;white-space:nowrap;">${escapeHtml(callNumber)}</a>
                ${tail}
              </p>`;
}

// ─── Sections ─────────────────────────────────────────────────────────────────

/** White masthead: logo left, brand promise right (hidden on phones). */
export function headerHtml(brand: BrandConfig): string {
  const mark = brand.logoUrl
    ? `<img src="${brand.logoUrl}" width="${brand.logoWidth}" alt="${escapeHtml(brand.name)}" border="0" style="display:block;border:0;outline:none;text-decoration:none;max-width:100%;height:auto;">`
    : `<span style="font-size:23px;font-weight:700;color:${brand.ink};">${escapeHtml(brand.name)}</span>`;
  return row(
    table(
      `<tr><td valign="middle">${mark}</td><td class="tagline" align="right" valign="middle" style="font-size:10px;line-height:16px;letter-spacing:1px;font-weight:700;color:${brand.primary};">${section("tagline", "YOUR POLICY.<br>YOUR PEACE OF MIND.")}</td></tr>`,
    ),
    "padding:28px 32px 12px;background:#ffffff;",
    "masthead",
  );
}

/** Welcome-email hero: MYB street scene with ink copy, BC navy grid with white copy. */
export function heroHtml(brand: BrandConfig, eyebrow: string, heading: string): string {
  const img = `${brand.assetBase}/${brand.hero.image}`;
  if (brand.hero.dark) {
    const copy = `<p style="margin:0 0 19px;font-size:11px;line-height:18px;letter-spacing:.7px;font-weight:700;text-transform:uppercase;color:#28624b;">${section("eyebrow", `<span style="display:inline-block;padding:5px 10px;background:#e1f3e9;border-radius:6px;">${escapeHtml(eyebrow)}</span>`)}</p><h1 class="hero-title" style="margin:0;color:#ffffff;font-size:40px;line-height:44px;letter-spacing:-1px;font-weight:700;">${escapeHtml(heading)}</h1>`;
    return row(
      table(`<tr><td align="left" class="hero-copy" style="padding:42px 36px 96px;">${copy}</td></tr>`),
      `background-color:${brand.hero.fill};background-image:url(${img});background-repeat:no-repeat;background-position:center bottom;background-size:cover;`,
      "hero",
    );
  }
  const copy = `<p style="margin:0 0 16px;font-size:11px;line-height:18px;letter-spacing:1.8px;font-weight:700;text-transform:uppercase;color:${brand.primary};">${section("eyebrow", escapeHtml(eyebrow))}</p><h1 class="hero-title" style="margin:0;color:${brand.ink};font-size:46px;line-height:49px;letter-spacing:-1.8px;font-weight:700;">${escapeHtml(heading)}</h1>`;
  return row(
    `<!--[if gte mso 9]><v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:640px;height:515px;"><v:fill type="frame" src="${img}" color="${brand.hero.fill}"/><v:textbox inset="0,0,0,0"><![endif]--><table role="presentation" width="100%" height="515" class="hero-layout" cellpadding="0" cellspacing="0" border="0" style="height:515px;"><tr><td align="center" valign="top" class="hero-copy" style="padding:32px 20px 0;">${copy}</td></tr></table><!--[if gte mso 9]></v:textbox></v:rect><![endif]-->`,
    `height:515px;vertical-align:top;text-align:center;background-color:${brand.hero.fill};background-image:url(${img});background-repeat:no-repeat;background-position:center bottom;background-size:100% auto;`,
    "hero hero-scene",
  );
}

/** One "Your policy" fact: icon file, label, already-escaped value. */
export type Fact = [icon: "dates" | "insurer" | "policy", label: string, value: string];

/** Welcome-email policy card: business name + shield, then icon fact rows. */
export function policyCardHtml(brand: BrandConfig, data: EmailData | undefined, facts: Fact[]): string {
  if (!data) return "";
  const all: Fact[] = [["dates", "Renewal date", longDate(data.renewalDate)], ...facts];
  const rows = all
    .map(
      ([icon, label, value]) =>
        `<tr><td width="40" valign="middle" style="width:40px;padding:12px 10px 12px 0;border-top:1px solid ${brand.line};">${art(brand, `fact-${icon}.png`, 28)}</td><td width="32%" valign="middle" style="padding:12px 12px 12px 0;border-top:1px solid ${brand.line};font-size:13px;line-height:21px;color:${brand.text};">${label}</td><td valign="middle" style="padding:12px 0;border-top:1px solid ${brand.line};font-size:14px;line-height:21px;font-weight:700;color:${brand.ink};overflow-wrap:anywhere;word-break:break-word;">${value}</td></tr>`,
    )
    .join("");
  return section(
    "policy",
    row(
      `${table(`<tr><td valign="middle"><p style="margin:0 0 6px;font-size:11px;line-height:16px;letter-spacing:1.6px;font-weight:700;color:${brand.primary};">YOUR POLICY</p><p style="margin:0;font-size:21px;line-height:27px;font-weight:700;color:${brand.ink};overflow-wrap:anywhere;word-break:break-word;">${escapeHtml(data.companyName)}</p></td><td width="70" style="padding-left:12px;">${art(brand, "shield.png", 58)}</td></tr>`)}<div style="height:17px;line-height:17px;font-size:1px;">&nbsp;</div>${table(rows)}`,
      "padding:28px 36px 8px;background:#ffffff;",
      "pad",
    ),
  );
}

/** The body copy row. */
export function bodyRow(brand: BrandConfig, inner: string, style = ""): string {
  return row(inner, `padding:24px 36px 6px;font-size:15px;line-height:24px;color:${brand.text};${style}`, "pad");
}

const APP_STORE = "https://apps.apple.com/gb/app/my-portal-policy-manager/id1542193943";
const GOOGLE_PLAY = "https://play.google.com/store/apps/details?id=com.opencomparisonltd.myportal";

/** Welcome-email "Meet the My Portal app" section with the store badges. */
export function portalHtml(brand: BrandConfig): string {
  const badge = (url: string, file: string, width: number, alt: string, pad: string) =>
    `<a href="${url}" aria-label="${alt}" style="display:inline-block;vertical-align:middle;margin:0 8px 8px 0;padding:${pad};text-decoration:none;">${art(brand, file, width, alt)}</a>`;
  return section(
    "portal",
    row(
      `<p style="margin:0 0 10px;font-size:11px;line-height:16px;letter-spacing:1.6px;font-weight:700;color:${brand.primary};">MEET THE MY PORTAL APP</p>${headline(brand, "Your insurance.", "All in one handy app.")}<p style="margin:17px 0 0;font-size:15px;line-height:25px;color:${brand.text};">All your policy documents are safely stored in My Portal. Download the app to keep everything together, wherever your working day takes you.</p>${art(brand, "portal-pocket.png", 430, "Your policy and documents together on your phone", "width:100%;max-width:430px;margin:17px auto 10px;")}<div style="text-align:center;">${badge(APP_STORE, "app-store-badge.png", 144, "Download on the App Store", "12px 0")}${badge(GOOGLE_PLAY, "google-play-badge.png", 185, "Get it on Google Play", "0")}</div>`,
      `padding:33px 36px 19px;background:#ffffff;border-top:1px solid ${brand.line};`,
      "pad",
    ),
  );
}

/** Welcome-email closing: trade photos, two-line sign-off, then the team name. */
export function closingHtml(brand: BrandConfig): string {
  const photo = (file: string, alt: string, caption: string, side: string) =>
    `<td width="50%" valign="top" style="padding-${side}:7px;">${art(brand, file, 260, alt, "width:100%;border-radius:14px;")}<p style="margin:10px 0 0;font-size:13px;line-height:19px;font-weight:700;color:${brand.ink};">${caption}</p></td>`;
  return row(
    `${section(
      "closing",
      `<p style="margin:0 0 10px;font-size:11px;line-height:16px;letter-spacing:1.6px;font-weight:700;color:${brand.primary};">BACK TO WHAT YOU DO BEST</p><h2 style="margin:0 0 23px;font-size:30px;line-height:34px;letter-spacing:-.8px;font-weight:700;color:${brand.ink};">Here’s to your next<br><span style="color:${brand.primary};">great working day.</span></h2>${table(`<tr>${photo("carpenter.jpg", "Carpenter at work", "For the makers.", "right")}${photo("electricians.jpg", "Electricians at work", "And the problem-solvers.", "left")}</tr>`)}<p style="margin:25px 0 0;font-size:17px;line-height:25px;font-weight:700;color:${brand.ink};">${brand.closing[0]}<br><span style="color:${brand.primary};">${brand.closing[1]}</span></p>`,
    )}<p style="margin:12px 0 0;font-size:13px;line-height:21px;color:${brand.text};">The ${escapeHtml(brand.name)} Team</p>`,
    `padding:31px 36px;text-align:center;background:${brand.soft};border-top:1px solid ${brand.line};`,
    "pad",
  );
}

export function footerHtml(args: {
  brandName: string;
  year: number;
  /** When true, append the Insurance Premium Tax note (PRICED emails). */
  iptNote: boolean;
  /** Service (non-marketing) emails carry the keep-with-your-documents line. */
  service: boolean;
}): string {
  const ipt = args.iptNote
    ? `\n              <p style="margin:10px 0 0;">All prices include Insurance Premium Tax (IPT) at 12%.</p>`
    : "";
  const serviceLine = args.service
    ? section("service", `\n              <p style="margin:10px 0 0;">This is a service email about your insurance policy. Please keep it with your policy documents.</p>`)
    : "";
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:640px;width:100%;">
          <tr>
            <td style="padding:24px 25px;text-align:center;font-size:11px;line-height:18px;color:#656579;">
              <p style="margin:0 0 10px;">&copy; ${args.year} ${escapeHtml(args.brandName)}. All rights reserved.</p>
              <p style="margin:0;">${escapeHtml(args.brandName)} is a trading name of Business Insurance Solutions Limited, authorised and regulated by the Financial Conduct Authority (FRN 513256). Registered in England and Wales No. 06837377. Registered office: 1st Floor, 2 Bishops Wharf, Walnut Tree Close, Guildford, GU1 4UP.</p>${ipt}${serviceLine}
            </td>
          </tr>
        </table>`;
}

// ─── Document ─────────────────────────────────────────────────────────────────

const MOBILE_CSS = (brand: BrandConfig) =>
  `body{margin:0!important;padding:0!important;width:100%!important;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%}table{border-collapse:separate;border-spacing:0;mso-table-lspace:0pt;mso-table-rspace:0pt}td{font-family:${brand.font},Arial,Helvetica,sans-serif}img{-ms-interpolation-mode:bicubic}a[x-apple-data-detectors]{color:inherit!important;text-decoration:none!important}strong{color:${brand.ink}}@media only screen and (max-width:600px){.outer{padding:0!important}.pad{padding-left:24px!important;padding-right:24px!important}.masthead{padding:22px 24px!important}.masthead img{width:180px!important}.tagline{display:none!important}.hero-scene,.hero-layout{height:370px!important}.hero-copy{padding-top:27px!important}.hero-title{font-size:36px!important;line-height:40px!important;letter-spacing:-1.2px!important}.headline-art{width:64px!important}.headline-art img{width:56px!important}.pay-figure{font-size:36px!important;line-height:42px!important}.quote-pad{padding:14px!important}.quote-mark{display:none!important}}@media only screen and (max-width:380px){.pad{padding-left:18px!important;padding-right:18px!important}.hero-scene,.hero-layout{height:330px!important}.hero-title{font-size:31px!important;line-height:35px!important}.headline-art{display:none!important}.quote-price{font-size:17px!important}}`;

export function documentHead(brand: BrandConfig): string {
  const faces = brand.fontFaces
    .map(
      (f) =>
        `@font-face{font-family:${brand.font};src:url("${brand.assetBase}/${f.file}") format("${f.format}");font-weight:${f.weight};font-style:normal;font-display:swap}`,
    )
    .join("");
  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<meta name="x-apple-disable-message-reformatting" />
<meta name="format-detection" content="telephone=no,address=no,email=no,date=no" />
<meta name="color-scheme" content="light" />
<meta name="supported-color-schemes" content="light" />
<title>${escapeHtml(brand.name)}</title>
<!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
<!--[if !mso]><!--><style>${faces}</style><!--<![endif]-->
<!--[if mso]><style>body,table,td,p,h1,h2,a,span{font-family:Arial,Helvetica,sans-serif!important}</style><![endif]-->
<style>${MOBILE_CSS(brand)}</style>
</head>`;
}

export function shellOpen(brand: BrandConfig): string {
  return `<body style="margin:0;padding:0;background:${brand.page};color:${brand.ink};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${brand.page}">
    <tr>
      <td align="center" class="outer" style="padding:32px 12px;">
        <!--[if mso]><table role="presentation" width="640" cellpadding="0" cellspacing="0" border="0"><tr><td><![endif]-->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="max-width:640px;width:100%;table-layout:fixed;">`;
}

/** Closes the white card, then the legal footer sits on the page colour. */
export function shellClose(legalHtml: string): string {
  return `        </table>
        ${legalHtml}
        <!--[if mso]></td></tr></table><![endif]-->
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/** Applies the brand font to every inline style, as the welcome emails do. */
export const withBrandFont = (brand: BrandConfig, html: string): string =>
  html.replace(/style="/g, `style="font-family:${brand.font},Arial,Helvetica,sans-serif;`);

/** One complete email: masthead, hero, policy card, content, My Portal, closing, legal. */
export function emailDocument(args: {
  brand: BrandConfig;
  /** Small label above the hero heading, by email type. */
  eyebrow: string;
  heading: string;
  /** Policy card HTML (empty for marketing emails). */
  policyCard: string;
  rows: string;
  iptNote: boolean;
  /** Marketing (win-back / anniversary): no My Portal section, no service line. */
  marketing: boolean;
}): string {
  const { brand } = args;
  const year = new Date().getUTCFullYear();
  return withBrandFont(
    brand,
    `${documentHead(brand)}
${shellOpen(brand)}
${headerHtml(brand)}
${heroHtml(brand, args.eyebrow, args.heading)}
${args.policyCard}
${args.rows}
${args.marketing ? "" : portalHtml(brand)}
${closingHtml(brand)}
${shellClose(footerHtml({ brandName: brand.name, year, iptNote: args.iptNote, service: !args.marketing }))}`,
  );
}
